﻿/**
 * Global singleton browser media player.
 * Module-level refs persist across component mounts/unmounts, so the player
 * keeps running while navigating between pages.
 *
 * App.vue owns the <audio> / <video> DOM elements and calls `registerAudioEl` /
 * `registerVideoEl` to hand their refs in. All other components just import this
 * composable and call the play/pause/seek helpers.
 */
import { ref, computed, watch } from 'vue';
import type { MediaLibraryItem } from '../stores/mediaPlayers';
import { canPlayMediaLocally, getMediaStreamUrl } from '../services/mediaLibraryUrls';

// ── DOM element refs (set by App.vue) ───────────────────────────────────────
const _audioEl = ref<HTMLAudioElement | null>(null);
const _videoEl = ref<HTMLVideoElement | null>(null);

export function registerAudioEl(el: HTMLAudioElement | null) {
  _audioEl.value = el;
}
export function registerVideoEl(el: HTMLVideoElement | null) {
  _videoEl.value = el;
}

// ── Playback state ───────────────────────────────────────────────────────────
export const currentItem = ref<MediaLibraryItem | null>(null);
export const browserSrc = ref('');
const browserAccessToken = ref('');
export const browserPlaying = ref(false);
export const browserCurrentTime = ref(0);
export const browserDuration = ref(0);
export const browserVolume = ref<number>(parseFloat(localStorage.getItem('player_volume') ?? '0.8'));
export const browserMuted = ref<boolean>(localStorage.getItem('player_muted') === 'true');
export const showVideoPopup = ref(false);

// ── Queue ────────────────────────────────────────────────────────────────────
export const playerQueue = ref<MediaLibraryItem[]>([]);
export const playerQueueIndex = ref(-1);

function persistVolume() {
  localStorage.setItem('player_volume', String(browserVolume.value));
  localStorage.setItem('player_muted', String(browserMuted.value));
}

watch(browserVolume, persistVolume);
watch(browserMuted, persistVolume);

// The active element for the current media type
const activeEl = computed<HTMLAudioElement | HTMLVideoElement | null>(() =>
  currentItem.value?.mediaType === 'video' ? _videoEl.value : _audioEl.value,
);

// ── Event handlers (called from the template in App.vue) ─────────────────────
export function onTimeUpdate() {
  browserCurrentTime.value = activeEl.value?.currentTime ?? 0;
}
export function onMetadata() {
  browserDuration.value = activeEl.value?.duration ?? 0;
  applyVolume();
}
export function onEnded() {
  browserPlaying.value = false;
  browserCurrentTime.value = 0;
  // Auto-advance queue
  if (playerQueueIndex.value >= 0 && playerQueueIndex.value < playerQueue.value.length - 1) {
    const nextIdx = playerQueueIndex.value + 1;
    const nextItem = playerQueue.value[nextIdx];
    playerQueueIndex.value = nextIdx;
    _startPlayItem(nextItem);
  }
}

// ── Controls ─────────────────────────────────────────────────────────────────
export function applyVolume() {
  const el = activeEl.value;
  if (!el) return;
  el.volume = browserVolume.value;
  el.muted = browserMuted.value;
}

export function toggleBrowserPlay() {
  const el = activeEl.value;
  if (!el) return;
  if (browserPlaying.value) el.pause();
  else el.play().catch(() => {});
}

export function seekTo(t: number) {
  const el = activeEl.value;
  if (!el) return;
  el.currentTime = t;
  browserCurrentTime.value = t;
}

export function seekRelative(delta: number) {
  seekTo(Math.max(0, Math.min(browserDuration.value, browserCurrentTime.value + delta)));
}

export function onVolumeInput(v: number) {
  browserVolume.value = v;
  browserMuted.value = v === 0;
  applyVolume();
}

export function stopBrowser() {
  const el = activeEl.value;
  if (el) {
    el.pause();
    try {
      el.currentTime = 0;
    } catch {
      // Some media elements can reject seeking before metadata is loaded.
    }
  }
  browserPlaying.value = false;
  browserCurrentTime.value = 0;
}

export function closeBrowserPlayer() {
  const el = activeEl.value;
  if (el) el.pause();
  browserSrc.value = '';
  browserAccessToken.value = '';
  currentItem.value = null;
  browserPlaying.value = false;
  browserCurrentTime.value = 0;
  browserDuration.value = 0;
  showVideoPopup.value = false;
  playerQueue.value = [];
  playerQueueIndex.value = -1;
}

function getBrowserStreamUrl(item: MediaLibraryItem): string | null {
  // External sources (e.g. Plex) carry their own absolute stream URL.
  if (item.url) return item.url;
  return getMediaStreamUrl(item.id, browserAccessToken.value || null);
}

// Internal: start playing an item directly (no toggle, no queue update)
function _startPlayItem(item: MediaLibraryItem) {
  const streamUrl = getBrowserStreamUrl(item);
  if (!streamUrl) {
    console.warn('[BrowserPlayer] Stream unavailable', {
      itemId: item.id,
      hasExternalUrl: !!item.url,
      canPlayMediaLocally: canPlayMediaLocally(),
    });
    browserSrc.value = '';
    browserPlaying.value = false;
    return;
  }

  currentItem.value = item;
  browserSrc.value = '';
  browserCurrentTime.value = 0;
  browserDuration.value = 0;
  browserPlaying.value = false;
  showVideoPopup.value = item.mediaType === 'video';
  // Let Vue render the new src, then play
  setTimeout(() => {
    browserSrc.value = streamUrl;
    setTimeout(() => {
      activeEl.value?.play().catch((e) => console.warn('[BrowserPlayer] play() blocked:', e));
    }, 50);
  }, 30);
}

// ── Queue helpers ─────────────────────────────────────────────────────────────
export function addToQueue(item: MediaLibraryItem) {
  playerQueue.value.push(item);
}

export function clearQueue() {
  playerQueue.value = [];
  playerQueueIndex.value = -1;
}

export function playFromQueue(index: number) {
  if (index < 0 || index >= playerQueue.value.length) return;
  playerQueueIndex.value = index;
  _startPlayItem(playerQueue.value[index]);
}

export function skipNext() {
  if (playerQueueIndex.value < playerQueue.value.length - 1) {
    playFromQueue(playerQueueIndex.value + 1);
  }
}

export function skipPrevious() {
  if (browserCurrentTime.value > 3) {
    seekTo(0);
  } else if (playerQueueIndex.value > 0) {
    playFromQueue(playerQueueIndex.value - 1);
  } else {
    seekTo(0);
  }
}

export async function playInBrowser(
  item: MediaLibraryItem,
  token: string,
  { nextTick, force = false }: { nextTick: () => Promise<void>; force?: boolean },
): Promise<boolean> {
  // Toggle if same item
  if (!force && currentItem.value?.id === item.id) {
    toggleBrowserPlay();
    return true;
  }

  browserAccessToken.value = token;
  const streamUrl = item.url ? item.url : getMediaStreamUrl(item.id, token);
  if (!streamUrl) {
    console.warn('[BrowserPlayer] Cannot play — no playable source', {
      itemId: item.id,
      hasExternalUrl: !!item.url,
      canPlayMediaLocally: canPlayMediaLocally(),
    });
    browserSrc.value = '';
    browserPlaying.value = false;
    currentItem.value = null;
    showVideoPopup.value = false;
    return false;
  }

  currentItem.value = item;
  browserSrc.value = '';
  browserCurrentTime.value = 0;
  browserDuration.value = 0;
  browserPlaying.value = false;
  showVideoPopup.value = item.mediaType === 'video';

  await nextTick();
  browserSrc.value = streamUrl;
  // Two ticks: first to bind :src, second for video popup to mount
  await nextTick();
  await nextTick();
  activeEl.value?.play().catch((e) => console.warn('[BrowserPlayer] play() blocked:', e));
  return true;
}

export async function setQueueAndPlay(
  items: MediaLibraryItem[],
  startIndex: number,
  token: string,
  { nextTick }: { nextTick: () => Promise<void> },
): Promise<boolean> {
  if (items.length === 0) return false;
  const safeIndex = Math.max(0, Math.min(startIndex, items.length - 1));
  playerQueue.value = [...items];
  playerQueueIndex.value = safeIndex;
  return playInBrowser(items[safeIndex], token, { nextTick, force: true });
}

export function formatPlayerTime(s: number): string {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

// Default export as composable for components that prefer `const player = useBrowserPlayer()`
export function useBrowserPlayer() {
  return {
    currentItem,
    browserSrc,
    browserPlaying,
    browserCurrentTime,
    browserDuration,
    browserVolume,
    browserMuted,
    showVideoPopup,
    playerQueue,
    playerQueueIndex,
    registerAudioEl,
    registerVideoEl,
    onTimeUpdate,
    onMetadata,
    onEnded,
    applyVolume,
    toggleBrowserPlay,
    seekTo,
    seekRelative,
    onVolumeInput,
    stopBrowser,
    closeBrowserPlayer,
    playInBrowser,
    setQueueAndPlay,
    addToQueue,
    clearQueue,
    playFromQueue,
    skipNext,
    skipPrevious,
    formatPlayerTime,
  };
}
